import type { CategoryDocument } from '@util/firestore/categories';
import type { ListingsFilters, PaginationFilters } from './useProductsPage';

export type ProductsPageUrlState = {
  topFilters: PaginationFilters;
  sideFilters: ListingsFilters;
  page: number;
};

const arrayKeys = ['brand', 'condition', 'deals', 'useCases'] as const;
const managedKeys = [...arrayKeys, 'category', 'minPrice', 'maxPrice', 'sort', 'limit', 'view', 'page', 'additional'];

export function getProductsPageDefaults(asPath: string, initial: ProductsPageUrlState): ProductsPageUrlState {
  const path = asPath.split(/[?#]/)[0];
  return {
    topFilters: {
      sort: path === '/collections/new-arrivals' ? 'newest' : 'ranked',
      limit: 25,
      // The page owns its initial layout; custom /p/ pages also use list.
      view: initial.topFilters.view,
    },
    sideFilters: {
      minPrice: null,
      maxPrice: null,
      category: path.startsWith('/c/') ? initial.sideFilters.category : null,
      brand: path.startsWith('/brands/') ? initial.sideFilters.brand : [],
      condition: [],
      deals: path === '/collections/featured' ? ['Featured Listings'] : path === '/collections/auctions' ? ['Auctions'] : [],
      useCases: [],
      additional: {},
    },
    page: 1,
  };
}

function cleanValues(value: string | string[] | undefined): string[] {
  return (Array.isArray(value) ? value : value ? [value] : []).filter(v => v.trim() !== '');
}

function cleanAdditional(value: unknown): NonNullable<ListingsFilters['additional']> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).flatMap<[string, string | string[]]>(([key, values]) => {
    if (!key || key === '__proto__' || key === 'constructor' || key === 'prototype') return [];
    if (typeof values === 'string' && values.trim()) return [[key, values]];
    if (Array.isArray(values) && values.every(v => typeof v === 'string')) {
      const cleaned = cleanValues(values);
      if (cleaned.length) return [[key, cleaned]];
    }
    return [];
  }));
}

/** Uses the server parser's names; dynamic route parameters never enter this query. */
export function readProductsPageUrl(
  asPath: string,
  defaults: ProductsPageUrlState,
  categories: CategoryDocument[]
): ProductsPageUrlState {
  const query = new URL(asPath, 'https://listing.invalid').searchParams;
  const sideFilters = { ...defaults.sideFilters };
  for (const key of arrayKeys) {
    sideFilters[key] = query.has(key) ? cleanValues(query.getAll(key)).filter(value => value !== '__all__') : cleanValues(defaults.sideFilters[key]);
  }
  if (query.has('category')) {
    sideFilters.category = categories.find(c => c.name === query.get('category')) ?? null;
  }
  for (const key of ['minPrice', 'maxPrice'] as const) {
    const value = query.get(key);
    const number = value?.trim() ? Number(value) : NaN;
    sideFilters[key] = Number.isFinite(number) && number >= 0 ? number : null;
  }
  if (query.has('additional')) {
    try {
      sideFilters.additional = cleanAdditional(JSON.parse(query.get('additional')!));
    } catch {
      sideFilters.additional = {};
    }
  }
  const sort = query.get('sort');
  const limit = Number(query.get('limit'));
  const view = query.get('view');
  const page = Number(query.get('page'));
  return {
    topFilters: {
      sort: sort === 'priceAsc' || sort === 'priceDesc' || sort === 'newest' || sort === 'ranked' ? sort : defaults.topFilters.sort,
      limit: limit === 10 || limit === 25 || limit === 50 ? limit : defaults.topFilters.limit,
      view: view === 'grid' || view === 'list' ? view : defaults.topFilters.view,
    },
    sideFilters,
    page: Number.isSafeInteger(page) && page > 0 ? page : 1,
  };
}

export function writeProductsPageUrl(asPath: string, state: ProductsPageUrlState, defaults: ProductsPageUrlState): string {
  const url = new URL(asPath, 'https://listing.invalid');
  // SSR may already include the requested view in its initial props. Keep
  // that explicit choice so the next direct visit cannot fall back to a
  // different page default after another filter is changed.
  const explicitView = url.searchParams.get('view');
  for (const key of managedKeys) url.searchParams.delete(key);
  for (const key of arrayKeys) {
    const values = cleanValues(state.sideFilters[key]);
    if (JSON.stringify(values) !== JSON.stringify(cleanValues(defaults.sideFilters[key]))) {
      values.forEach(value => url.searchParams.append(key, value));
      if (!values.length && cleanValues(defaults.sideFilters[key]).length) url.searchParams.set(key, '__all__');
    }
  }
  const category = state.sideFilters.category?.name;
  if (category && category !== defaults.sideFilters.category?.name) url.searchParams.set('category', category);
  // A category route supplies an implicit category. Retain an explicit clear
  // without an empty query value (the server already treats unknown names as null).
  if (!category && defaults.sideFilters.category) url.searchParams.set('category', '__all__');
  for (const key of ['minPrice', 'maxPrice'] as const) {
    const value = state.sideFilters[key];
    if (value !== null && Number.isFinite(value)) url.searchParams.set(key, String(value));
  }
  for (const key of ['sort', 'limit', 'view'] as const) {
    if (state.topFilters[key] !== defaults.topFilters[key] || (key === 'view' && (explicitView === 'list' || explicitView === 'grid'))) {
      url.searchParams.set(key, String(state.topFilters[key]));
    }
  }
  if (state.page > 1) url.searchParams.set('page', String(state.page));
  const additional = cleanAdditional(state.sideFilters.additional);
  if (Object.keys(additional).length) url.searchParams.set('additional', JSON.stringify(additional));
  return `${url.pathname}${url.search}${url.hash}`;
}
